/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { Theme } from '@coursera/cds-core';
import { Typography2 } from '@coursera/cds-core';
import { Textarea } from '@coursera/coursera-ui';

import GradeNotification from 'bundles/compound-assessments/components/form-parts/GradeNotification';
import type { FormPartsValidationStatus } from 'bundles/compound-assessments/components/form-parts/lib/checkResponsesInvalid';
import { typeNames } from 'bundles/compound-assessments/constants';
import type { ReflectPrompt, ReflectResponse } from 'bundles/compound-assessments/types/FormParts';

import _t from 'i18n!nls/compound-assessments';

import ReadOnlyText from '../ReadOnlyText';

export const styles = {
  textAreaContainer: css({
    // Textarea from CUI has extra 21px on the top. This is to offset that
    marginTop: -21,
  }),
  subtitle: (theme: Theme) =>
    css({
      marginTop: 'var(--cds-spacing-50)',
    }),
};

type Props = {
  prompt?: ReflectPrompt;
  response?: ReflectResponse;
  onChangeResponse: (response: ReflectResponse) => void;
  isDisabled: boolean;
  isReadOnly: boolean;
};

export const checkInvalid = (): FormPartsValidationStatus | null => null;

class TextAreaBox extends React.Component<Props> {
  onChange = (event: React.SyntheticEvent<HTMLInputElement>) => {
    const { onChangeResponse } = this.props;
    const { value } = event.target as HTMLInputElement;

    onChangeResponse({
      typeName: typeNames.AUTO_GRADABLE_RESPONSE,
      definition: {
        value: {
          answer: value,
        },
      },
    });
  };

  render() {
    const { response, prompt, isDisabled, isReadOnly } = this.props;
    const answer = response?.definition?.value?.answer || '';
    const isPartialFeedback = prompt?.variant?.detailLevel === 'Partial';

    if (!prompt) {
      return null;
    }

    return (
      <div>
        {isReadOnly ? (
          <div>
            {!isPartialFeedback && <ReadOnlyText isMultiLine={true}>{answer}</ReadOnlyText>}
            <GradeNotification prompt={prompt} />
          </div>
        ) : (
          <div css={styles.textAreaContainer}>
            <Textarea
              value={answer}
              disabled={isDisabled}
              placeholder={_t('What do you think?')}
              onChange={this.onChange}
              componentId={`text-area-${prompt.id}`}
              nativeHtmlAttributes={{
                'aria-describedby': `caption-${prompt.id}`,
                'aria-labelledby': `prompt-${prompt.id}`,
              }}
            />
            <Typography2
              component="p"
              variant="bodySecondary"
              id={`caption-${prompt.id}`}
              color="supportText"
              css={styles.subtitle}
            >
              {_t('Your answer cannot be more than 10000 characters.')}
            </Typography2>
          </div>
        )}
      </div>
    );
  }
}

export default TextAreaBox;
